import React from 'react';
import { CODE_128_QUIET_ZONE_MODULES, encodeCode128B } from './code128';

const MAX_MODULE_WIDTH_PX = 3;

type Bar = { start: number; width: number };

const toBars = (modules: string): Bar[] =>
  Array.from(modules.matchAll(/1+/g), match => ({
    start: match.index ?? 0,
    width: match[0].length,
  }));

export type Code128BarcodeProps = {
  value: string;
  heightPx: number;
  /** Widest the symbol may be, quiet zones included. */
  maxWidthPx: number;
};

/**
 * A Code 128 (set B) barcode as an SVG. Modules are as wide as fits, up to
 * three CSS pixels, so a long id narrows instead of overflowing.
 */
export const Code128Barcode = ({
  value,
  heightPx,
  maxWidthPx,
}: Code128BarcodeProps) => {
  const modules = encodeCode128B(value);
  const totalModules = modules.length + CODE_128_QUIET_ZONE_MODULES * 2;
  const moduleWidth = Math.min(MAX_MODULE_WIDTH_PX, maxWidthPx / totalModules);
  const width = totalModules * moduleWidth;
  return (
    <svg
      role="img"
      aria-label={`Barcode ${value}`}
      width={width}
      height={heightPx}
      viewBox={`0 0 ${width} ${heightPx}`}
      shapeRendering="crispEdges"
      style={{ display: 'block', maxWidth: '100%' }}
    >
      <rect width={width} height={heightPx} fill="#fff" />
      <g fill="#000">
        {toBars(modules).map(bar => (
          <rect
            key={bar.start}
            x={(bar.start + CODE_128_QUIET_ZONE_MODULES) * moduleWidth}
            y={0}
            width={bar.width * moduleWidth}
            height={heightPx}
          />
        ))}
      </g>
    </svg>
  );
};
